<script lang="ts" setup>
import Menu from 'primevue/menu'
import type { CartItem } from '~/types/commerce'
import { useItemDisplay } from '~/composables/useItemDisplay'

interface Props {
  item: CartItem
  context?: 'cart' | 'order'
  showActions?: boolean
  showImage?: boolean
  compactView?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  context: 'cart',
  showActions: false,
  showImage: false,
  compactView: false,
})

const emit = defineEmits<{
  increase: [item: CartItem]
  decrease: [item: CartItem]
  edit: [cartUuid: string]
  remove: [cartUuid: string]
}>()

const { t } = useNuxtApp().$i18n
const menu = ref()

const {
  hasModifierSelections,
  getModifierSelections,
  formatModifierPrice,
  getItemModifiers,
  hasCustomizations,
} = useItemDisplay()

const menuItems = computed(() => [
  {
    label: t('Add one'),
    icon: 'pi pi-plus',
    command: () => emit('increase', props.item),
  },
  {
    label: t('Remove one'),
    icon: 'pi pi-minus',
    command: () => emit('decrease', props.item),
  },
  {
    separator: true,
  },
  {
    label: t('Edit'),
    icon: 'pi pi-pencil',
    command: () => emit('edit', props.item.cart_uuid),
  },
  {
    label: t('Remove'),
    icon: 'pi pi-trash',
    class: 'text-red-500',
    command: () => emit('remove', props.item.cart_uuid),
  },
])

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

// Blink animation on quantity change
const isBlinking = ref(false)

watch(() => props.item.quantity, () => {
  isBlinking.value = true
  setTimeout(() => {
    isBlinking.value = false
  }, 400)
})

// Get product data based on context
const productData = computed(() => {
  if (props.context === 'order') {
    return props.item.product_data || props.item
  }
  return props.item
})

// Get modifiers from the appropriate location
const modifiers = computed(() => getItemModifiers(props.item))

// Check if we should show the total line
const showTotal = computed(() => {
  if (props.context === 'order') {
    return productData.value.total && hasCustomizations(props.item)
  }
  return true
})

// Compact display: single line with modifiers
const compactModifiersText = computed(() => {
  const parts: string[] = []

  if (modifiers.value.length > 0) {
    modifiers.value.forEach((modifier) => {
      if (hasModifierSelections(modifier)) {
        const selections = getModifierSelections(modifier)
        selections.forEach((selection) => {
          parts.push(selection.name)
        })
      }
    })
  }

  return parts.length > 0 ? ` - ${parts.join(', ')}` : ''
})
</script>

<template>
  <div :class="['product-item-renderer', { 'product-item-blink': isBlinking }]">
    <!-- Compact View -->
    <div
      v-if="compactView"
      class="flex items-center gap-2"
    >
      <div class="grow text-sm">
        <span class="font-semibold">
          <template v-if="productData.quantity > 1">{{ productData.quantity }} x </template>{{ productData.name }}
        </span>
        <span class="text-gray-600">{{ compactModifiersText }}</span>
        <span
          v-if="item.notes"
          class="text-gray-500 italic ml-2"
        >📝 {{ item.notes }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="font-semibold text-sm whitespace-nowrap">
          {{ usePriceFormatter(productData.total || 0) }}
        </span>
        <Button
          icon="pi pi-ellipsis-v"
          class="p-button-text p-button-rounded !p-1"
          size="small"
          :aria-label="t('Actions')"
          @click="toggleMenu"
        />
        <Menu
          ref="menu"
          :model="menuItems"
          :popup="true"
        />
      </div>
    </div>

    <!-- Normal View -->
    <div
      v-else
      class="flex gap-2"
    >
      <!-- Product Image -->
      <div
        v-if="showImage && item.images"
        class="flex-shrink-0"
      >
        <img
          class="size-24 rounded-md"
          :src="getFirstImage(item) || '/product_placeholder.webp'"
          :alt="`Image of ${productData.name}`"
        >
      </div>

      <!-- Product Content -->
      <div class="grow">
        <!-- Header with name and price -->
        <div class="flex">
          <div class="grow pb-1 text-base">
            <span class="font-semibold">
              {{ productData.quantity }} x {{ productData.name }}
            </span>
          </div>
          <div class="flex justify-end">
            <span v-if="productData.quantity">{{ productData.quantity }} x </span>
            <span class="ml-2">
              {{ usePriceFormatter(productData.base_price || productData.price || 0) }}
            </span>
          </div>
        </div>

        <!-- Modifiers -->
        <div
          v-if="modifiers.length > 0 && modifiers.some(m => hasModifierSelections(m))"
          class="mt-1"
        >
          <ul class="list-none p-0 pl-6 text-sm">
            <template
              v-for="modifier in modifiers"
              :key="modifier.group_id"
            >
              <template v-if="hasModifierSelections(modifier)">
                <li
                  v-for="selection in getModifierSelections(modifier)"
                  :key="selection.id"
                  class="flex justify-between py-1"
                >
                  <span class="text-gray-700">{{ selection.name }}</span>
                  <span
                    v-if="(selection.price_modifier || selection.price_change || 0) > 0"
                    class="text-black font-medium"
                  >
                    {{ formatModifierPrice(selection.price_modifier || selection.price_change || 0) }}
                  </span>
                </li>
              </template>
            </template>
          </ul>
        </div>

        <!-- Legacy custom items (if any) -->
        <div
          v-if="productData.custom && productData.custom.length > 0"
          class="mt-2"
        >
          <span class="pl-4 font-semibold text-sm">{{ $t('Extras') }}:</span>
          <ul class="list-none p-0 pl-8 text-sm">
            <li
              v-for="customItem in productData.custom"
              :key="customItem.id"
              class="flex justify-between py-1"
            >
              <span>{{ customItem.name }}</span>
              <span
                v-if="customItem.price"
                class="text-black"
              >
                {{ usePriceFormatter(customItem.price) }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Total -->
        <div
          v-if="showTotal"
          class="mt-2 pt-2 text-right border-t border-gray-200"
        >
          <span class="font-semibold">
            {{ context === 'order' ? $t('Total') + ': ' : '' }}
            {{ usePriceFormatter(productData.total || 0) }}
          </span>
        </div>

        <!-- Action Buttons (Expanded mode shows circular buttons) -->
        <div
          v-if="showActions"
          class="mt-2 flex justify-end gap-1"
        >
          <Button
            class="p-button-rounded p-button-danger p-2"
            icon="pi pi-minus"
            :aria-label="$t('Remove one')"
            @click="emit('decrease', item)"
          />
          <Button
            class="p-button-rounded border-green-500 bg-green-500 p-2 hover:border-green-700"
            icon="pi pi-plus"
            :aria-label="$t('Add one')"
            @click="emit('increase', item)"
          />
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded p-2"
            :aria-label="$t('Edit Product')"
            @click="emit('edit', item.cart_uuid)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger p-2"
            :aria-label="$t('Remove')"
            @click="emit('remove', item.cart_uuid)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.product-item-renderer {
  @apply w-full transition-colors duration-300;
}

.product-item-blink {
  animation: blink-highlight 400ms ease-out;
}

@keyframes blink-highlight {
  0% {
    background-color: var(--p-primary-color, #22c55e);
  }
  100% {
    background-color: transparent;
  }
}

/* Print styles */
@media print {
  .product-item-renderer {
    @apply text-xs;
  }

  .product-item-renderer .text-base {
    @apply text-xs;
  }

  .product-item-renderer .text-sm {
    @apply text-xs;
  }

  /* Improve modifier text contrast */
  .product-item-renderer .text-gray-700 {
    @apply text-black;
  }
}
</style>
